import React, { useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Grid, LinearProgress, Stack,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import AlertContext from '../../../../Context/AlertContext';
import Elements from '../../../Elements';
import Images from '../../../../Configs/images';
import { getDashboardOverview } from '../../../../Redux/Models/dashboard';

export default function Overview({
  classes, error, message, overview, isLoading,
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { openAlert } = useContext(AlertContext);

  useEffect(() => {
    if (error) {
      openAlert({
        message: `Something went wrong. ${message}`,
        severity: 'error',
      });
    }
  }, [error]);

  useEffect(() => {
    dispatch(getDashboardOverview());
  }, []);

  const {
    request, order, wallet, courier, storage,
  } = overview;

  return (
    <Elements.AdminPageBase navigate={navigate}>
      <Box className={classes.root}>
        {isLoading
        && <LinearProgress color="inherit" />}
        <Grid container spacing={2}>
          <Grid item md={12} lg={7}>
            <Box px={2} py={3} bgcolor="#FDFDFD" borderRadius="10px">
              <Box display="flex" justifyContent="space-between">
                <Box color="#2E3C40" fontWeight="700">Request Order In Progress</Box>
                <Box className="mute">
                  1
                  {' '}
                  {moment().format('MMM YYYY')}
                  {' '}
                  -
                  {' '}
                  {moment().format('DD MMM YYYY')}
                </Box>
              </Box>

              <Stack direction="row" spacing={2} mt={2}>
                <OverviewCard
                  title="Total Request In"
                  value={request.in.total}
                  percentage={request.in.percent}
                  desc="from 1 month ago"
                  image={Images.OenoIn}
                />
                <OverviewCard
                  title="Total Request Out"
                  value={request.out.total}
                  percentage={request.out.percent}
                  desc="from 1 month ago"
                  image={Images.OenoOut}
                />
                <OverviewCard
                  title="Total Request Canceled"
                  value={request.cancel.total}
                  percentage={request.cancel.percent}
                  desc="from 1 month ago"
                  image={Images.OenoCanceled}
                />
              </Stack>
            </Box>
            <Box px={2} py={3} bgcolor="#FDFDFD" borderRadius="10px" mt={2}>
              <Box display="flex" justifyContent="space-between">
                <Box color="#2E3C40" fontWeight="700">Order List - OenoMarket</Box>
                <Box className="mute">
                  1
                  {' '}
                  {moment().format('MMM YYYY')}
                  {' '}
                  -
                  {' '}
                  {moment().format('DD MMM YYYY')}
                </Box>
              </Box>

              <Stack direction="row" spacing={2} mt={2}>
                <OverviewCard
                  title="Total Items On Sale"
                  value={order.sale.total}
                  percentage={order.sale.percent}
                  desc="from 1 month ago"
                  image={Images.OenoBag}
                />
                <OverviewCard
                  title="Total Sale Transaction"
                  value={order.transaction.total}
                  percentage={order.transaction.percent}
                  desc="from 1 month ago"
                  image={Images.OenoCurrency}
                />
                <OverviewCard
                  title="Total Active Bid"
                  value={order.bid_active.total}
                  percentage={order.bid_active.percent}
                  desc="from 1 month ago"
                  image={Images.OenoHammer}
                />
              </Stack>
              <Stack direction="row" spacing={2} mt={2}>
                <OverviewCard
                  title="Total Bid"
                  value={order.bid.total}
                  percentage={order.bid.percent}
                  desc="from 1 month ago"
                  image={Images.OenoBox}
                />
                <OverviewCard
                  title="Total Seller"
                  value={order.seller.total}
                  percentage={order.seller.percent}
                  desc="from 1 month ago"
                  image={Images.OenoPeople}
                />
                <OverviewCard
                  title="Total Shipping from Market"
                  value={0}
                  percentage={0}
                  desc="from 1 month ago"
                  image={Images.OenoShipping}
                />
              </Stack>
            </Box>
            <Box px={2} py={3} bgcolor="#FDFDFD" borderRadius="10px" mt={2}>
              <Box display="flex" justifyContent="space-between">
                <Box color="#2E3C40" fontWeight="700">Total Balance</Box>
                <Box className="mute">
                  1
                  {' '}
                  {moment().format('MMM YYYY')}
                  {' '}
                  -
                  {' '}
                  {moment().format('DD MMM YYYY')}
                </Box>
              </Box>

              <Stack direction="row" spacing={2} mt={2}>
                <OverviewCard
                  title="Total Balance"
                  value={wallet.user.total}
                  percentage={wallet.user.percent}
                  desc="from 1 month ago"
                  image={Images.OenoWallet}
                />
                <OverviewCard
                  title="Total Revenue"
                  value={wallet.revenue.total}
                  percentage={wallet.revenue.percent}
                  desc="from 1 month ago"
                  image={Images.OenoTopup2}
                />
                <Box flex={1} />
              </Stack>
            </Box>
          </Grid>
          <Grid item md={12} lg={5}>
            <Box px={2} py={3} bgcolor="#FDFDFD" borderRadius="10px">
              <Box display="flex" justifyContent="space-between">
                <Box color="#2E3C40" fontWeight="700">HQ Storage Availability</Box>
                <Box color="#38AC3D" fontWeight="700">
                  {storage.total}
                  {' '}
                  Storages
                </Box>
              </Box>

              <Stack direction="row" spacing={2} mt={2}>
                <Elements.Progress
                  used={storage.used}
                  total={storage.available}
                  full={!storage.available}
                  size={50}
                />
                <OverviewCard
                  title="Total In Use"
                  value={storage.used}
                />
                <OverviewCard
                  title="Total Availability"
                  value={storage.available}
                />
              </Stack>
              <Box
                p={1.5}
                boxShadow="0px 1px 4px rgba(0, 0, 0, 0.06), 0px 2px 4px rgba(34, 34, 34, 0.04)"
                borderRadius="5px"
                flex={1}
                mt={2}
              >
                <Box display="flex">
                  <Box flex={1}>
                    <Box fontSize={12} className="mute">Customer Storage</Box>
                    <Box fontSize={18} fontWeight="700" my={0.5}>60</Box>
                  </Box>
                  <Box flex={1}>
                    <Box fontSize={12} className="mute">HQ Storage</Box>
                    <Box fontSize={18} fontWeight="700" my={0.5}>60</Box>
                  </Box>
                  <Box flex={1}>
                    <Box fontSize={12} className="mute">Deficiency</Box>
                    <Box fontSize={18} fontWeight="700" my={0.5} color="#F24848">-20</Box>
                  </Box>
                </Box>
              </Box>
            </Box>

            <Box px={2} py={3} bgcolor="#FDFDFD" borderRadius="10px" mt={2}>
              <Box display="flex" justifyContent="space-between">
                <Box color="#2E3C40" fontWeight="700">Courier Availability</Box>
                <Box color="#38AC3D" fontWeight="700">
                  {courier.total}
                  {' '}
                  Couriers
                </Box>
              </Box>

              <Stack direction="row" spacing={2} mt={2}>
                <OverviewCard
                  title="In Delivery"
                  value={courier.used}
                />
                <OverviewCard
                  title="Available Courier"
                  value={courier.available}
                />
              </Stack>
            </Box>

            <Box px={2} py={3} bgcolor="#FDFDFD" borderRadius="10px" mt={2}>
              <Box display="flex" justifyContent="space-between">
                <Box color="#2E3C40" fontWeight="700">Request Item - OenoMarket</Box>
                <Box className="mute">
                  1
                  {' '}
                  {moment().format('MMM YYYY')}
                  {' '}
                  -
                  {' '}
                  {moment().format('DD MMM YYYY')}
                </Box>
              </Box>

              <Stack direction="row" spacing={2} mt={2}>
                <OverviewCard
                  title="Total Requested Items"
                  value="20"
                  percentage={25}
                  desc="from 1 month ago"
                />
                <OverviewCard
                  title="Total Items Found"
                  value="5"
                  percentage={2}
                  desc="from 1 month ago"
                />
              </Stack>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Elements.AdminPageBase>
  );
}

export function OverviewCard(props) {
  const {
    title, image, value, percentage = 0, desc,
  } = props;

  const minus = percentage < 0;
  return (
    <Box
      p={1.5}
      boxShadow="0px 1px 4px rgba(0, 0, 0, 0.06), 0px 2px 4px rgba(34, 34, 34, 0.04)"
      borderRadius="5px"
      display="flex"
      gap={1}
      alignItems="center"
      flex={1}
    >
      {!!image
      && <img src={image} alt={title} height="18" width="18" />}
      <Box>
        <Box fontSize={12} className="mute">{title}</Box>
        <Box fontSize={18} fontWeight="700" my={0.5}>{value}</Box>
        {percentage >= 0
        && (
        <Box fontSize={12} className="mute">
          <Box display="inline" color={minus ? '#F24848' : '#38AC3D'}>
            {minus ? '-' : '+'}
            {percentage}
            %
          </Box>
          {' '}
          {desc}
        </Box>
        )}
      </Box>
    </Box>
  );
}
